import React, { useCallback, useEffect } from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { applicationsActions } from '@app/redux/applications/applications.reducer';
import { getApplicationDetails } from '@app/redux/applications/applications.selectors';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';

import Loader from '@components/loader';

import AccountDetailsSkeleton from '@pages/Applications/components/AccountDetailsSkeleton';

type WithApplicationValidationsProps = {
	showLoader?: boolean;
	applicationStatusRefresh: ({
		applicationId,
		formId,
		formVersion,
	}: {
		applicationId: number;
		formId: number;
		formVersion?: number | null | undefined;
	}) => Promise<void>;
};

function withApplicationValidations<TProps extends WithApplicationValidationsProps>(
	WrappedComponent: React.JSXElementConstructor<TProps>,
) {
	return function RenderProps(props: TProps) {
		const dispatch = useAppDispatch();
		const applicationDetails = useAppSelector(getApplicationDetails);

		const { isPending: isLoadingGenerateSignLinkValidation, startRequest: generateSignLinkValidation } =
			useTriggeredRequest(AccountClientRequests.Applications.generateSignLinkValidation);

		const { isPending: isLoadingRecertifyW8BenFormValidation, startRequest: recertifyW8BenFormValidation } =
			useTriggeredRequest(AccountClientRequests.Applications.recertifyW8BenFormValidation);

		const { isPending: isLoadingDownloadApplication, startRequest: getDownloadApplicationValidation } =
			useTriggeredRequest(AccountClientRequests.Applications.downloadApplicationValidation);

		const { isPending: isLoadingDownloadTaxForm, startRequest: getDownloadTaxFormValidation } = useTriggeredRequest(
			AccountClientRequests.Applications.downloadTaxFormValidation,
		);

		const { isPending: isLoadingGetSigningListPDF, startRequest: getSigningListPDF } = useTriggeredRequest(
			AccountClientRequests.Applications.getSigningListPDF,
		);

		const application_id = applicationDetails?.id;
		const custodian_request_id = applicationDetails?.current_custodian_request?.id;

		const handleFetchGenerateSignLink = useCallback(async () => {
			if (custodian_request_id && application_id) {
				try {
					await generateSignLinkValidation({ custodian_request_id, application_id, type: 'tax-form' });
				} catch (e) {
					console.error(e);
				}
			} else {
				dispatch(applicationsActions.clearGenerateSignLinkData());
			}
		}, [custodian_request_id, application_id, dispatch, generateSignLinkValidation]);

		const handleFetchRecertifyW8BenForm = useCallback(async () => {
			try {
				if (application_id) {
					await recertifyW8BenFormValidation({ application_id });
				}
			} catch (e) {
				console.error(e);
			}
		}, [application_id, recertifyW8BenFormValidation]);

		const handleFetchSigningListPDF = useCallback(async () => {
			try {
				if (application_id) {
					await getSigningListPDF({ application_id, type: 'tax-form' });
				}
			} catch (e) {
				console.error(e);
			}
		}, [application_id, getSigningListPDF]);

		const handleFetchDownloadValidation = useCallback(
			async (
				type: 'application' | 'tax-form',
				callback: (data: { application_id: number; type: 'application' | 'tax-form' }) => Promise<unknown>,
			) => {
				try {
					if (application_id) {
						await callback({ application_id, type });
					}
				} catch (e) {
					console.error(e);
				}
			},
			[application_id],
		);

		useEffect(() => {
			handleFetchGenerateSignLink();
			handleFetchRecertifyW8BenForm();
			handleFetchSigningListPDF();
			handleFetchDownloadValidation('application', getDownloadApplicationValidation);
			handleFetchDownloadValidation('tax-form', getDownloadTaxFormValidation);
		}, [
			handleFetchGenerateSignLink,
			handleFetchRecertifyW8BenForm,
			handleFetchSigningListPDF,
			handleFetchDownloadValidation,
			getDownloadApplicationValidation,
			getDownloadTaxFormValidation,
		]);

		return isLoadingGenerateSignLinkValidation ||
			isLoadingRecertifyW8BenFormValidation ||
			isLoadingGetSigningListPDF ||
			isLoadingDownloadApplication ||
			isLoadingDownloadTaxForm ||
			props.showLoader ? (
			<Loader loaderFallBackComponent={AccountDetailsSkeleton} />
		) : (
			<WrappedComponent {...props} />
		);
	};
}

export default withApplicationValidations;
